import React, { useContext, createContext } from "react";
import {
  MainProfileFragment,
  CurrentUserFragment,
  SpaceMetadataFragment,
} from "~/graphql/codegen/graphql";
import type { SwitchPhase } from "~/utils/organisation-switch";

/** FOX-6291 Part A, from the root loader: see organisation-switch.server.ts. */
export type OrganisationSwitchContext = {
  phase: SwitchPhase;
  /** `YYYY-MM-DD`, or null when this space has no switch day. */
  switchDay: string | null;
  /** The current space's creation time (ISO). */
  spaceCreatedAt: string | null;
  /** The viewer opened the moved page from the Moved item. */
  movedSeen: boolean;
  /** The viewer dismissed the one-time Transformation Engine intro (A5). */
  engineIntroSeen: boolean;
};

export const MainContext = React.createContext<{
  authenticated: boolean;
  isDefaultPlatformSpace: boolean;
  currentSpace: SpaceMetadataFragment;
  user?: CurrentUserFragment | null;
  profile?: MainProfileFragment | null;
  tags: string[];
  // True when the user is enrolled in at least one program cohort across all
  // their mindspaces (root loader's mindspaces.all.programs.enrolled.total).
  // Gates the AI coach surfaces.
  hasEnrolledPrograms: boolean;
  // True when THIS VIEWER can open at least one published (or completed) event in
  // the CURRENT Mindspace, past or future — root loader's
  // mindspace.events.hasPublishedEvents. Viewer-scoped: a space whose only events
  // belong to cohorts the viewer is not in answers false, which is the whole
  // point (an always-true tab over an empty /events page was the bug).
  // A bare scalar rather than a field on `currentSpace` on purpose: the
  // SpaceMetadata fragment is spread over every Mindspace in the space switcher,
  // so putting it there would cost one existence probe per space per page load.
  // Gates the Events (and, additionally, Organisers) left-nav items.
  hasPublishedEvents: boolean;
  organisationSwitch: OrganisationSwitchContext;
  /** A5: the viewer dismissed the engine intro this visit (before the loader knows). */
  markEngineIntroSeen: () => void;
  updateTheme: (theme: "" | "light" | "dark") => void;
}>({
  isDefaultPlatformSpace: false,
  currentSpace: {
    id: "",
    name: "",
    uniqueName: "",
    icon: "",
    images: {
      thumbnail: "",
    },
    modules: {
      objectives: false,
      events: false,
      resources: true,
      programs: false,
      sandbox: false,
      sandboxHistory: false,
      aiDisclaimer: false,
      aiDisclaimerText: "",
      aiDisclaimerDetail: "",
      // These two are NOT false like the module flags above. They mirror
      // MINDSPACE_CAPABILITY_DEFAULTS on the server (see
      // server/domains/mindspace/capabilities.ts): absent means ENABLED, so
      // this pre-loader placeholder context can never be the reason a learner
      // loses the AI coach or customer support.
      liveAvatarCoachEnabled: true,
      customerSupportEnabled: true,
    },
  },
  authenticated: false,
  tags: [],
  hasEnrolledPrograms: false,
  hasPublishedEvents: false,
  organisationSwitch: {
    phase: "before",
    switchDay: null,
    spaceCreatedAt: null,
    movedSeen: false,
    engineIntroSeen: false,
  },
  markEngineIntroSeen: () => {},
  updateTheme: () => {},
});

export const useIsDefaultPlatformSpace = () =>
  useContext(MainContext).isDefaultPlatformSpace;
export const useCurrentSpace = () => useContext(MainContext).currentSpace;
export const useIsAuthenticated = () => useContext(MainContext).authenticated;
export const useUser = () => useContext(MainContext).user;
export const useProfile = () => useContext(MainContext).profile;
export const useTags = () => useContext(MainContext).tags;
export const useHasEnrolledPrograms = () =>
  useContext(MainContext).hasEnrolledPrograms;
export const useHasPublishedEvents = () =>
  useContext(MainContext).hasPublishedEvents;
export const useOrganisationSwitch = () =>
  useContext(MainContext).organisationSwitch;
export const useMarkEngineIntroSeen = () => useContext(MainContext).markEngineIntroSeen;
export const useUpdateTheme = () => useContext(MainContext).updateTheme;
